import { ArcDirection, FrameRect, LonLat, Pt, TradeMapVariant } from './types';

// Pure maths of the trade map: sizes, arcs and the frame. No d3, no DOM, so
// every rule here is unit-tested cheaply. The projection itself is geometry.ts.

/** Addendum 8: partner dot radius on combined completed volume. Never clamped. */
export const partnerRadius = (received: number, sent: number): number =>
  3 + 2.3 * Math.sqrt(Math.max(received + sent, 0));

/**
 * Addendum 4: stroke width encodes package count, 0.8 + 0.5 x count, with NO
 * ceiling (review Blocking, 2026-09-23). A cap would make every partner past it
 * identical, the failure mode the sibling ticket named. A test pins that 1, 2,
 * 3, 5 and 11 packages are all distinct.
 */
export const arcWidth = (count: number): number => 0.8 + 0.5 * Math.max(count, 0);

/**
 * Addendum 4: the quadratic control point sits perpendicular to the chord at
 * 22% of its projected length, at least 16px out. Inbound arcs bow UP the map
 * (negative y), outbound arcs bow DOWN, so both directions to one partner stay
 * visible as a lens. A vertical chord has no up; its normal points left.
 */
export const arcControl22 = (from: Pt, to: Pt, direction: ArcDirection): Pt => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  let nx = -dy / length;
  let ny = dx / length;
  if (ny > 0 || (ny === 0 && nx > 0)) {
    nx = -nx;
    ny = -ny;
  }
  const offset = Math.max(length * 0.22, 16) * (direction === 'inbound' ? 1 : -1);
  return { x: (from.x + to.x) / 2 + nx * offset, y: (from.y + to.y) / 2 + ny * offset };
};

export const arcPathD = (from: Pt, control: Pt, to: Pt): string =>
  `M${from.x} ${from.y}Q${control.x} ${control.y} ${to.x} ${to.y}`;

/** Point on the quadratic at t in [0, 1] */
export const quadraticPoint = (from: Pt, control: Pt, to: Pt, t: number): Pt => {
  const u = 1 - t;
  return {
    x: u * u * from.x + 2 * u * t * control.x + t * t * to.x,
    y: u * u * from.y + 2 * u * t * control.y + t * t * to.y,
  };
};

/** Arc length by sampling (the SVG uses pathLength=1; kept for the evidence and any future canvas use) */
export const quadraticLength = (from: Pt, control: Pt, to: Pt, samples: number = 24): number => {
  let length = 0;
  let previous = from;
  for (let i = 1; i <= samples; i += 1) {
    const point = quadraticPoint(from, control, to, i / samples);
    length += Math.hypot(point.x - previous.x, point.y - previous.y);
    previous = point;
  }
  return length;
};

/**
 * Where the places must land, per variant. Desktop keeps the header row clear
 * at the top; mobile keeps the header row and the compact legend clear.
 */
export const frameRect = (variant: TradeMapVariant, width: number, height: number): FrameRect => {
  if (variant === 'mobile') return { left: 12, right: width - 12, top: 36, bottom: height - 40 };
  return { left: 28, right: width - 28, top: 44, bottom: height - 28 };
};

/** Addendum 2: the frame never spans less than this many degrees of longitude */
export const MIN_LON_SPAN = 25;

/** The home longitude when there is one, else the mean of the places */
export const frameCentreLon = (home: LonLat | null, points: LonLat[]): number => {
  if (home) return home[0];
  if (points.length === 0) return 0;
  return points.reduce((sum, [lon]) => sum + lon, 0) / points.length;
};

export const meanLat = (points: LonLat[]): number =>
  points.length === 0 ? 0 : points.reduce((sum, [, lat]) => sum + lat, 0) / points.length;

/**
 * The zoom clamp as two synthetic points: centre +/- half the minimum span on
 * the places' mean latitude. fitExtent on the padded set can never frame less
 * than MIN_LON_SPAN degrees, and a single place (or places on one meridian) can
 * never degenerate into an infinite scale. Places already wider than the span
 * are unaffected: the pads fall inside their box.
 */
export const padToMinLonSpan = (points: LonLat[], centreLon: number, lat: number): LonLat[] => [
  ...points,
  [centreLon - MIN_LON_SPAN / 2, lat],
  [centreLon + MIN_LON_SPAN / 2, lat],
];
